// THE BALLOT BUILDER — the operator's create form (`POST …/ballot`, P3a-2). One
// `.page-narrow` column (the SetupPage Frame supplies it): ballot-level choices
// first, then a stacked list of item cards, then one primary "Create this draft".
//
// ── IT IS A THIN FORM OVER `spec.ts` ───────────────────────────────────────
// Every correctness answer — is the draft ready, what fraction a motion's base
// derives, what the wire spec is — lives in `./spec.ts`, pure and drilled by
// `spec.test.ts`. This file holds the shape, the focus management and the copy, none
// of which this package can test without a DOM (README). So the rules that decide
// whether the binding CREATE is offered are NOT here: they are `validateDraft`, and
// this component only renders its answer.
//
// ── SETUP RUNS AHEAD OF THE VOTING SCREEN, AND SAYS SO ─────────────────────
// The registrar's ruling (2026-08-29) is FULL SCOPE — positions and motions, every
// worker-supported option. The cast screen (CastPage) today serves only single-item,
// one-seat, first-past-the-post ELECTIONS, so a motion, an approval or multi-seat
// election, or a multi-item ballot cannot yet be PUT to a voter. Where a config is
// not yet votable this form shows an INLINE NOTE, never a block: the draft is lawful
// and creatable; the voting screen simply has not caught up.
//
// ── THE FRACTION IS NEVER TYPED ────────────────────────────────────────────
// A motion operator picks the majority BASE — the yardstick SENTENCE a member reads
// (`thresholds.ts`) — and `spec.ts` derives the wire fraction from it
// (docs/decides/BUILDER-THRESHOLD-DERIVATION.md). No numerator or denominator is
// ever shown.
//
// ── A MOTION'S OPERATIVE WORDS ARE A FIELD, AND THE ONLY UNBOUNDED ONE ─────
// `instrument` is the motion itself — the words a member reads on the paper and
// consents to — so it is a textarea high in the motion card, above every setting,
// and it is the one field here with NO length cap: the first binding ballot is two
// constitutional amendments, and a form that refused a lawful motion for running to
// paragraphs would be worse than no form. `mover` and `seconder` sit low with the
// class of motion, marked "(optional)", and block nothing.

import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import { useFocusOnMount } from '../a11y/useFocusOnMount';
import { ConfirmBlock } from './ConfirmBlock';
import { refusalSentence } from './copy';
import {
  buildSpec,
  initialBallotDraft,
  newCandidate,
  newMotionItem,
  newPositionItem,
  validateDraft,
  type BallotDraft,
  type BallotKind,
  type DraftProblem,
  type HouseRule,
  type ItemDraft,
  type MotionItemDraft,
  type PositionItemDraft,
  type Secrecy,
} from './spec';
import { THRESHOLD_COPY } from '../thresholds';
import { ELECTORATE_SENTENCE } from '../paper';
import { positionVotableInApp } from '../cast-votability';
import { METHOD_COPY } from '../results/PositionResult';
import type { CreateBallotSpec } from '../api/client';
import type { Electorate, MajorityBase } from '@cca/decides-count';

// ── The option metadata, keyed on the engine's own unions where there is one ──

interface RadioOption<V extends string> {
  value: V;
  label: string;
  help?: string;
  /** A clause that must not be skimmed, rendered bold within the help sentence —
   *  the yardstick's `emphasis` ("Not voting counts as a No."). `| undefined` is
   *  explicit so the yardstick's own optional `emphasis` can be assigned straight in
   *  under exactOptionalPropertyTypes. */
  emphasis?: string | undefined;
}

const KIND_OPTIONS: readonly RadioOption<BallotKind>[] = [
  {
    value: 'position',
    label: 'An election',
    help: 'Fills one or more positions from a list of nominees.',
  },
  { value: 'motion', label: 'A motion', help: 'Puts a question to Synod, to carry or to lose.' },
];

const HOUSE_RULE_OPTIONS: readonly RadioOption<HouseRule>[] = [
  { value: 'one-tally', label: 'One tally', help: 'Votes are counted in a single combined tally.' },
  {
    value: 'separate-chambers',
    label: 'Separate chambers',
    help: 'The Clergy and the Laity are counted separately.',
  },
];

/** Who may vote — help sentences are the paper's own `ELECTORATE_SENTENCE`, so the
 *  builder and the ballot paper cannot drift on who elects a position (SO 15.8). */
const ELECTORATE_OPTIONS: readonly RadioOption<Electorate>[] = [
  { value: 'clergy', label: 'The Clergy', help: ELECTORATE_SENTENCE.clergy },
  { value: 'laity', label: 'The Laity', help: ELECTORATE_SENTENCE.laity },
  { value: 'both', label: 'Clergy and Laity together', help: ELECTORATE_SENTENCE.both },
];

/** STV is registered in the schema but not implemented (it fails closed at the
 *  count), so it is not offered — the same exclusion the worker's `METHODS_ENABLED`
 *  makes. The two help sentences are the results sheet's own words, taken from its
 *  now-exported `METHOD_COPY` (`results/PositionResult.tsx`) so the builder and the
 *  results sheet cannot drift; its `stv` key is never read here, as this form must
 *  never offer it. */
const METHOD_OPTIONS: readonly RadioOption<'fptp' | 'approval'>[] = [
  { value: 'fptp', label: 'First past the post', help: METHOD_COPY.fptp },
  { value: 'approval', label: 'Approval', help: METHOD_COPY.approval },
];

/** The three bases, each rendering its live yardstick sentence from `THRESHOLD_COPY`
 *  so the operator picks the SENTENCE, not a fraction (the fraction is derived). */
const MAJORITY_BASE_OPTIONS: readonly RadioOption<MajorityBase>[] = [
  {
    value: 'present-and-voting',
    label: 'Those present and voting',
    help: THRESHOLD_COPY['present-and-voting'].sentence,
    emphasis: THRESHOLD_COPY['present-and-voting'].emphasis,
  },
  {
    value: 'summoned-per-house',
    label: 'The members summoned, per House',
    help: THRESHOLD_COPY['summoned-per-house'].sentence,
    emphasis: THRESHOLD_COPY['summoned-per-house'].emphasis,
  },
  {
    value: 'all-synod-members',
    label: 'Every member of Synod',
    help: THRESHOLD_COPY['all-synod-members'].sentence,
    emphasis: THRESHOLD_COPY['all-synod-members'].emphasis,
  },
];

/** The consequence wording is `api/client.ts`'s account of `secrecy`
 *  (CastPaperView.ballot.secrecy): an attributable vote is recorded against the
 *  member's name and published with the result, and they are told so before they
 *  mark. */
const SECRECY_OPTIONS: readonly RadioOption<Secrecy>[] = [
  {
    value: 'secret',
    label: 'Secret',
    help: 'No vote is shown against anyone. This is the ordinary ballot.',
  },
  {
    value: 'attributable',
    label: 'Attributable',
    help: 'Each member’s vote is recorded against their name and published with the result — and they are told so before they mark.',
  },
];

/** The motion classes the worker's `MOTION_CLASSES` admits, plus "Not stated" (→
 *  null). Values are the worker's literals verbatim (`ballot-create.ts`); the class
 *  is a recorded label and is not required. */
const MOTION_CLASS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: 'Not stated' },
  { value: 'ordinary-motion', label: 'Ordinary motion' },
  { value: 'canon', label: 'Canon' },
  { value: 'constitutional-amendment', label: 'Constitutional amendment' },
  { value: 'winding-up', label: 'Winding-up' },
  { value: 'bishop-removal', label: 'Removal of a Bishop' },
];

const NOT_VOTABLE_NOTE =
  'Members can’t yet vote on this in the app; setup is ahead of the voting screen.';

interface Props {
  busy: boolean;
  /** The wire code of the last create refusal, or `null`. */
  error: string | null;
  onCreate: (spec: CreateBallotSpec) => void;
  onCancel: () => void;
}

export function BallotBuilder({ busy, error, onCreate, onCancel }: Props): JSX.Element {
  const [draft, setDraft] = useState<BallotDraft>(initialBallotDraft);
  const [leaving, setLeaving] = useState(false);
  const [changingKind, setChangingKind] = useState(false);
  const requestFocus = usePendingFocus();

  // Focus the heading on entry — the FocusHeading pattern (a tabIndex={-1} heading
  // focused on mount), so the keyboard lands on the title rather than dropping to
  // <body>.
  const heading = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(heading);

  const problems = validateDraft(draft);
  const valid = problems.length === 0;
  const dirty = draft.title.trim() !== '' || draft.items.length > 0;
  const kindLocked = draft.items.length > 0;
  const noun = draft.kind === 'motion' ? 'motion' : 'election';
  const titleError = problems.find((p) => p.at === 'title');

  const addItem = (): void => {
    const item = draft.kind === 'motion' ? newMotionItem() : newPositionItem();
    requestFocus(`item-${item.key}-title`);
    setDraft((d) => ({ ...d, items: [...d.items, item] }));
  };
  const updateItem = (index: number, item: ItemDraft): void => {
    setDraft((d) => ({ ...d, items: d.items.map((it, i) => (i === index ? item : it)) }));
  };
  const removeItem = (index: number): void => {
    requestFocus('ballot-add-item');
    setDraft((d) => ({ ...d, items: d.items.filter((_, i) => i !== index) }));
  };
  const confirmChangeKind = (): void => {
    setChangingKind(false);
    // Two kinds, so "change" toggles. Clearing the items is the point — a motion card
    // cannot carry a position's fields — and secrecy resets to the default.
    setDraft((d) => ({
      ...d,
      kind: d.kind === 'position' ? 'motion' : 'position',
      items: [],
      secrecy: 'secret',
    }));
  };
  const handleBack = (): void => {
    if (dirty) setLeaving(true);
    else onCancel();
  };

  return (
    <section className="setup-builder" aria-labelledby="builder-heading">
      {leaving ? (
        <ConfirmBlock
          prompt="This draft isn’t saved yet. Leave and lose it?"
          confirmLabel="Yes, leave without saving"
          busy={busy}
          onConfirm={onCancel}
          onCancel={() => setLeaving(false)}
        />
      ) : (
        <button
          type="button"
          className="btn btn-ghost setup-back"
          onClick={handleBack}
          disabled={busy}
        >
          Back to setup
        </button>
      )}

      <h1 className="ballot-title" id="builder-heading" tabIndex={-1} ref={heading}>
        Build a ballot
      </h1>
      <p className="setup-lede">
        Build an election to fill a position, or a motion to put a question to Synod. It is saved as
        a draft you can build up, mark ready and open — nothing here is put to a vote yet, and a
        draft can be abandoned.
      </p>

      {error !== null ? (
        <p className="alert" role="alert">
          {refusalSentence(error)}
        </p>
      ) : null}

      {/* onSubmit is prevented — Create is a deliberate button press, not the first
          Enter in a field. */}
      <form className="setup-builder-form" onSubmit={(event) => event.preventDefault()}>
        <div className="setup-field">
          <label htmlFor="ballot-title">Ballot title</label>
          <input
            id="ballot-title"
            className="setup-input"
            value={draft.title}
            disabled={busy}
            aria-describedby={titleError ? 'ballot-title-error' : undefined}
            onChange={(event) => setDraft((d) => ({ ...d, title: event.target.value }))}
          />
          {titleError ? <FieldError id="ballot-title-error" message={titleError.message} /> : null}
        </div>

        <RadioGroup<BallotKind>
          legend="What kind of ballot is this?"
          name="ballot-kind"
          options={KIND_OPTIONS}
          value={draft.kind}
          disabled={busy || kindLocked}
          onChange={(kind) => setDraft((d) => ({ ...d, kind }))}
        />
        {kindLocked ? (
          changingKind ? (
            <ConfirmBlock
              prompt={`Change this to ${draft.kind === 'position' ? 'a motion' : 'an election'} ballot? This clears the ${String(draft.items.length)} item${draft.items.length === 1 ? '' : 's'} you have added.`}
              confirmLabel="Yes, change kind and clear items"
              busy={busy}
              onConfirm={confirmChangeKind}
              onCancel={() => setChangingKind(false)}
            />
          ) : (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => setChangingKind(true)}
            >
              Change kind (clears items)
            </button>
          )
        ) : null}

        <RadioGroup<HouseRule>
          legend="How are the votes counted?"
          name="ballot-house-rule"
          options={HOUSE_RULE_OPTIONS}
          value={draft.houseRule}
          disabled={busy}
          onChange={(houseRule) => setDraft((d) => ({ ...d, houseRule }))}
        />

        <div className="setup-items">
          {draft.items.length === 0 ? (
            <p className="setup-panel-note">
              No {draft.kind === 'motion' ? 'motions' : 'elections'} on this ballot yet. Add the
              first one below.
            </p>
          ) : (
            draft.items.map((item, index) =>
              item.kind === 'position' ? (
                <PositionCard
                  key={item.key}
                  item={item}
                  index={index}
                  busy={busy}
                  problems={itemProblems(problems, index)}
                  onChange={(next) => updateItem(index, next)}
                  onRemove={() => removeItem(index)}
                />
              ) : (
                <MotionCard
                  key={item.key}
                  item={item}
                  index={index}
                  itemCount={draft.items.length}
                  isFirstMotion={index === 0}
                  secrecy={draft.secrecy}
                  busy={busy}
                  problems={itemProblems(problems, index)}
                  onChange={(next) => updateItem(index, next)}
                  onSecrecyChange={(secrecy) => setDraft((d) => ({ ...d, secrecy }))}
                  onRemove={() => removeItem(index)}
                />
              ),
            )
          )}
        </div>

        {draft.items.length > 1 ? (
          <AheadNote>
            Members can’t yet vote on a ballot with more than one item in the app; setup is ahead of
            the voting screen.
          </AheadNote>
        ) : null}

        <button
          type="button"
          id="ballot-add-item"
          className="btn btn-ghost setup-add"
          disabled={busy}
          onClick={addItem}
        >
          {draft.items.length === 0
            ? `Add ${noun === 'motion' ? 'a motion' : 'an election'}`
            : `Add another ${noun}`}
        </button>

        {!valid ? (
          <div className="setup-builder-blockers" id="builder-blockers">
            <p className="setup-panel-note">Before you can create this draft:</p>
            <ul className="setup-blocker-list">
              {problems.map((problem) => (
                <li key={problemKey(problem)}>{problem.message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <button
          type="button"
          className="btn btn-primary setup-primary setup-create"
          disabled={!valid || busy}
          aria-describedby={!valid ? 'builder-blockers' : undefined}
          onClick={() => {
            if (valid) onCreate(buildSpec(draft));
          }}
        >
          {busy ? 'Working…' : 'Create this draft'}
        </button>
      </form>
    </section>
  );
}

// ── One position item card ──────────────────────────────────────────────────

interface PositionCardProps {
  item: PositionItemDraft;
  index: number;
  busy: boolean;
  /** The problems addressed to THIS item (index already matched). */
  problems: DraftProblem[];
  onChange: (item: PositionItemDraft) => void;
  onRemove: () => void;
}

function PositionCard({
  item,
  index,
  busy,
  problems,
  onChange,
  onRemove,
}: PositionCardProps): JSX.Element {
  const requestFocus = usePendingFocus();
  const base = `item-${item.key}`;
  const titleError = problems.find((p) => p.at === 'item-title');
  const electorateError = problems.find((p) => p.at === 'electorate');
  const seatsError = problems.find((p) => p.at === 'seats');
  const altError = problems.find((p) => p.at === 'alternate-seats');
  const candidatesError = problems.find((p) => p.at === 'candidates');

  const addCandidate = (): void => {
    const candidate = newCandidate();
    requestFocus(`${base}-cand-${candidate.key}-name`);
    onChange({ ...item, candidates: [...item.candidates, candidate] });
  };
  const removeCandidate = (key: string): void => {
    requestFocus(`${base}-add-nominee`);
    onChange({ ...item, candidates: item.candidates.filter((c) => c.key !== key) });
  };

  // The cast screen's own rule (`../cast-votability.ts`), so this note and
  // CastPage's refusal cannot disagree about which elections a member can vote on.
  const notYetVotable = !positionVotableInApp(item);

  return (
    <fieldset className="card setup-item">
      <legend className="setup-item-legend">Election {index + 1}</legend>

      <div className="setup-field">
        <label htmlFor={`${base}-title`}>Title of this election</label>
        <input
          id={`${base}-title`}
          className="setup-input"
          value={item.title}
          disabled={busy}
          aria-describedby={titleError ? `${base}-title-error` : undefined}
          onChange={(event) => onChange({ ...item, title: event.target.value })}
        />
        {titleError ? <FieldError id={`${base}-title-error`} message={titleError.message} /> : null}
      </div>

      <RadioGroup<Electorate>
        legend="Who may vote in this election?"
        name={`${base}-electorate`}
        options={ELECTORATE_OPTIONS}
        value={item.electorate}
        disabled={busy}
        describedBy={electorateError ? `${base}-electorate-error` : undefined}
        onChange={(electorate) => onChange({ ...item, electorate })}
      />
      {electorateError ? (
        <FieldError id={`${base}-electorate-error`} message={electorateError.message} />
      ) : null}

      <div className="setup-field">
        <label htmlFor={`${base}-seats`}>Places to fill</label>
        <input
          id={`${base}-seats`}
          className="setup-input setup-input-num"
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={Number.isNaN(item.seats) ? '' : String(item.seats)}
          disabled={busy}
          aria-describedby={hintAndError(
            `${base}-seats-hint`,
            seatsError ? `${base}-seats-error` : null,
          )}
          onChange={(event) => onChange({ ...item, seats: event.currentTarget.valueAsNumber })}
        />
        <span className="setup-field-hint" id={`${base}-seats-hint`}>
          A whole number, 1 or more.
        </span>
        {seatsError ? <FieldError id={`${base}-seats-error`} message={seatsError.message} /> : null}
      </div>

      <div className="setup-field">
        <label htmlFor={`${base}-alt`}>Alternate places</label>
        <input
          id={`${base}-alt`}
          className="setup-input setup-input-num"
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          value={String(item.alternateSeats)}
          disabled={busy}
          aria-describedby={hintAndError(`${base}-alt-hint`, altError ? `${base}-alt-error` : null)}
          onChange={(event) => {
            const next = event.currentTarget.valueAsNumber;
            onChange({ ...item, alternateSeats: Number.isNaN(next) ? 0 : next });
          }}
        />
        <span className="setup-field-hint" id={`${base}-alt-hint`}>
          A whole number, 0 or more.
        </span>
        {altError ? <FieldError id={`${base}-alt-error`} message={altError.message} /> : null}
      </div>

      <RadioGroup<'fptp' | 'approval'>
        legend="How is it counted?"
        name={`${base}-method`}
        options={METHOD_OPTIONS}
        value={item.method}
        disabled={busy}
        onChange={(method) => onChange({ ...item, method })}
      />

      <fieldset className="setup-fieldset setup-candidates">
        <legend className="setup-legend">Nominees</legend>
        {candidatesError ? (
          <FieldError id={`${base}-candidates-error`} message={candidatesError.message} />
        ) : null}
        <ul className="setup-candidate-list">
          {item.candidates.map((candidate, candidateIndex) => {
            const nameError = problems.find(
              (p) => p.at === 'candidate-name' && p.candidateIndex === candidateIndex,
            );
            const nameId = `${base}-cand-${candidate.key}-name`;
            return (
              <li className="setup-candidate-row" key={candidate.key}>
                <div className="setup-field">
                  <label htmlFor={nameId}>Nominee’s name</label>
                  <input
                    id={nameId}
                    className="setup-input"
                    value={candidate.displayName}
                    disabled={busy}
                    aria-describedby={nameError ? `${nameId}-error` : undefined}
                    onChange={(event) =>
                      onChange({
                        ...item,
                        candidates: item.candidates.map((c) =>
                          c.key === candidate.key ? { ...c, displayName: event.target.value } : c,
                        ),
                      })
                    }
                  />
                  {nameError ? (
                    <FieldError id={`${nameId}-error`} message={nameError.message} />
                  ) : null}
                </div>
                {/* Discrete — a ghost, and only when there is more than one row: SO 15.6
                    keeps at least one nominee, so the last row has no Remove. */}
                {item.candidates.length > 1 ? (
                  <button
                    type="button"
                    className="btn btn-ghost setup-remove"
                    disabled={busy}
                    onClick={() => removeCandidate(candidate.key)}
                  >
                    Remove
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          id={`${base}-add-nominee`}
          className="btn btn-ghost"
          disabled={busy}
          onClick={addCandidate}
        >
          Add a nominee
        </button>
      </fieldset>

      {notYetVotable ? (
        <AheadNote>
          Members can’t yet vote on this election in the app — the voting screen takes elections
          that fill one place, with no alternate places, counted first past the post. Setup is ahead
          of it.
        </AheadNote>
      ) : null}

      <button
        type="button"
        className="btn btn-ghost setup-remove"
        disabled={busy}
        onClick={onRemove}
      >
        Remove this election
      </button>
    </fieldset>
  );
}

// ── One motion item card ────────────────────────────────────────────────────

interface MotionCardProps {
  item: MotionItemDraft;
  index: number;
  itemCount: number;
  /** Secrecy is BALLOT-level; it is offered on the FIRST motion card only, so two
   *  motion cards never render two controls over one value. */
  isFirstMotion: boolean;
  secrecy: Secrecy;
  busy: boolean;
  problems: DraftProblem[];
  onChange: (item: MotionItemDraft) => void;
  onSecrecyChange: (secrecy: Secrecy) => void;
  onRemove: () => void;
}

function MotionCard({
  item,
  index,
  itemCount,
  isFirstMotion,
  secrecy,
  busy,
  problems,
  onChange,
  onSecrecyChange,
  onRemove,
}: MotionCardProps): JSX.Element {
  const base = `item-${item.key}`;
  const titleError = problems.find((p) => p.at === 'item-title');
  const instrumentError = problems.find((p) => p.at === 'instrument');
  const baseError = problems.find((p) => p.at === 'majority-base');

  return (
    <fieldset className="card setup-item">
      <legend className="setup-item-legend">Motion {index + 1}</legend>

      <div className="setup-field">
        <label htmlFor={`${base}-title`}>The motion, as it is put</label>
        <input
          id={`${base}-title`}
          className="setup-input"
          value={item.title}
          disabled={busy}
          aria-describedby={titleError ? `${base}-title-error` : undefined}
          onChange={(event) => onChange({ ...item, title: event.target.value })}
        />
        {titleError ? <FieldError id={`${base}-title-error`} message={titleError.message} /> : null}
      </div>

      {/* THE OPERATIVE WORDS — the substance of the motion, so it sits directly under
          the title and above every setting: what a member reads and consents to comes
          before how it is counted. A TEXTAREA because a constitutional amendment runs
          to paragraphs, and with NO maxLength — the paper renders it verbatim and
          entire, and refusing a lawful motion for being wordy is the over-strict
          direction (Composition of Synod cl 9(1)-(2)). `rows` sets the resting height;
          `.setup-textarea` leaves it resizable downwards by the operator. */}
      <div className="setup-field">
        <label htmlFor={`${base}-instrument`}>The words Synod is asked to agree</label>
        <textarea
          id={`${base}-instrument`}
          className="setup-input setup-textarea"
          rows={6}
          value={item.instrument}
          disabled={busy}
          aria-describedby={hintAndError(
            `${base}-instrument-hint`,
            instrumentError ? `${base}-instrument-error` : null,
          )}
          onChange={(event) => onChange({ ...item, instrument: event.target.value })}
        />
        <span className="setup-field-hint" id={`${base}-instrument-hint`}>
          These are the words members read on the ballot paper before they mark. They are shown in
          full, exactly as typed, and are never shortened — paste the amendment or resolution
          entire.
        </span>
        {instrumentError ? (
          <FieldError id={`${base}-instrument-error`} message={instrumentError.message} />
        ) : null}
      </div>

      {isFirstMotion ? (
        <div>
          <RadioGroup<Secrecy>
            legend="Is this ballot secret or attributable?"
            name={`${base}-secrecy`}
            options={SECRECY_OPTIONS}
            value={secrecy}
            disabled={busy}
            onChange={onSecrecyChange}
          />
          {itemCount > 1 ? (
            <p className="setup-field-hint">Secrecy is set once, for the whole ballot.</p>
          ) : null}
        </div>
      ) : null}

      <RadioGroup<MajorityBase>
        legend="What must this motion carry?"
        name={`${base}-base`}
        options={MAJORITY_BASE_OPTIONS}
        value={item.majorityBase}
        disabled={busy}
        describedBy={baseError ? `${base}-base-error` : undefined}
        onChange={(majorityBase) => onChange({ ...item, majorityBase })}
      />
      {baseError ? <FieldError id={`${base}-base-error`} message={baseError.message} /> : null}

      {/* WHAT IS RECORDED, not what is decided — so it sits below the settings, with
          the class of motion, and each label says "(optional)" the way the abandon
          reason says "(required)" (BallotsPanel). Nothing here blocks: no ruling makes
          a mover or a seconder part of what Synod votes on, and `validateDraft`
          deliberately does not check them. */}
      <p className="setup-field-hint">Who moved and seconded this motion, where it is recorded.</p>

      <div className="setup-field">
        <label htmlFor={`${base}-mover`}>Mover (optional)</label>
        <input
          id={`${base}-mover`}
          className="setup-input"
          value={item.mover}
          disabled={busy}
          onChange={(event) => onChange({ ...item, mover: event.target.value })}
        />
      </div>

      <div className="setup-field">
        <label htmlFor={`${base}-seconder`}>Seconder (optional)</label>
        <input
          id={`${base}-seconder`}
          className="setup-input"
          value={item.seconder}
          disabled={busy}
          onChange={(event) => onChange({ ...item, seconder: event.target.value })}
        />
      </div>

      <div className="setup-field">
        <label htmlFor={`${base}-class`}>Class of motion</label>
        <select
          id={`${base}-class`}
          className="setup-input"
          value={item.motionClass ?? ''}
          disabled={busy}
          onChange={(event) =>
            onChange({
              ...item,
              motionClass: event.target.value === '' ? null : event.target.value,
            })
          }
        >
          {MOTION_CLASS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <AheadNote>{NOT_VOTABLE_NOTE}</AheadNote>

      <button
        type="button"
        className="btn btn-ghost setup-remove"
        disabled={busy}
        onClick={onRemove}
      >
        Remove this motion
      </button>
    </fieldset>
  );
}

// ── Shared bits ─────────────────────────────────────────────────────────────

/** A radio group — a real `<fieldset>`/`<legend>`, each option a `<label>` WRAPPING
 *  its input (implicit association, the ballot paper's `.vote-row` pattern). `value`
 *  may be `null` (nothing chosen yet), which is what a "choose a base" gate rests on. */
function RadioGroup<V extends string>({
  legend,
  name,
  options,
  value,
  onChange,
  disabled = false,
  describedBy,
}: {
  legend: string;
  name: string;
  options: readonly RadioOption<V>[];
  value: V | null;
  onChange: (value: V) => void;
  disabled?: boolean;
  // `| undefined` is explicit so a caller may pass the "no error id" ternary under
  // exactOptionalPropertyTypes; a bare `string?` would refuse the undefined arm.
  describedBy?: string | undefined;
}): JSX.Element {
  return (
    <fieldset className="setup-fieldset" aria-describedby={describedBy}>
      <legend className="setup-legend">{legend}</legend>
      <div className="setup-radio-set">
        {options.map((option) => (
          <label className="setup-radio" key={option.value}>
            <input
              className="radio"
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              disabled={disabled}
              onChange={() => onChange(option.value)}
            />
            <span className="setup-radio-text">
              <span className="setup-radio-label">{option.label}</span>
              {option.help !== undefined ? (
                <span className="setup-radio-help">
                  {option.help}
                  {option.emphasis !== undefined ? <strong>{` ${option.emphasis}`}</strong> : null}
                </span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** An inline field error — a `role="alert"` span the field points at with
 *  `aria-describedby`. The WORDS carry the meaning; nothing here is a colour. */
function FieldError({ id, message }: { id: string; message: string }): JSX.Element {
  return (
    <span className="setup-field-error" id={id} role="alert">
      {message}
    </span>
  );
}

/** A calm "setup is ahead of the voting screen" note — never a block. Same left
 *  stripe as the panel seam: a fact about what is coming, not a warning. */
function AheadNote({ children }: { children: ReactNode }): JSX.Element {
  return <p className="setup-panel-note setup-ahead">{children}</p>;
}

/** Join a hint id with an optional error id for one `aria-describedby` token list. */
function hintAndError(hintId: string, errorId: string | null): string {
  return errorId === null ? hintId : `${hintId} ${errorId}`;
}

/** The problems addressed to one item (its index already matched). */
function itemProblems(problems: DraftProblem[], index: number): DraftProblem[] {
  return problems.filter((p) => 'index' in p && p.index === index);
}

/** A stable React key for a blocker line — its address, not its position. */
function problemKey(problem: DraftProblem): string {
  const item = 'index' in problem ? `-${String(problem.index)}` : '';
  const candidate = 'candidateIndex' in problem ? `-${String(problem.candidateIndex)}` : '';
  return `${problem.at}${item}${candidate}`;
}

/**
 * Move keyboard focus to a stable element AFTER the render that added or removed a
 * row — so an added field takes focus and a removal lands on a stable anchor (never
 * <body>). The target is named by its DOM id, which is keyed on the row's stable
 * client key, so it resolves across the reorder a removal causes. The same intent as
 * SetupPage's FocusHeading, for a list that changes after mount rather than once on
 * it.
 */
function usePendingFocus(): (id: string) => void {
  const pending = useRef<string | null>(null);
  useEffect(() => {
    if (pending.current === null) return;
    const id = pending.current;
    pending.current = null;
    document.getElementById(id)?.focus();
  });
  return (id: string): void => {
    pending.current = id;
  };
}
